import { flagManagementTranslation } from '../config/translation'

export type FlagRecord = {
  entryId: string
  code: string
  name: string
  type: string
  geoMarkets: string[]
  roles: string[]
  users: string[]
  countries: string[]
  created: string
  createdBy: string
  modified: string
  modifiedBy: string
}

export type FlagRecordDependencyType = 'EPI' | 'UserRights'

export type FlagRecordDependencyMarket = {
  code: string
  market: string
}

export type FlagRecordDependency = {
  entryId: string
  flagCode: string
  market: string
  toolsFlagCode: string
  type: FlagRecordDependencyType
}

export type FlagRecordAssignmentSource =
  | 'WW_PartnerFlags'
  | 'WW_User_Tools_Access'
  | 'WW_UserRights'
  | 'WW_ContactCampaigns'

export type FlagRecordAssignmentSubject = 'Partners' | 'Contacts'

export type FlagRecordAssignmentScope = {
  source: FlagRecordAssignmentSource
  subjectLabel: FlagRecordAssignmentSubject
}

export type FlagRecordAssignmentCount = FlagRecordAssignmentScope & {
  count: number
}

export type FlagRecordFields = Pick<
  FlagRecord,
  'code' | 'name' | 'type' | 'geoMarkets' | 'roles' | 'users' | 'countries'
>

export type FlagRecordDependencyFields = {
  dependentEpiFlagRecords: FlagRecordDependencyMarket[]
  dependentToolRecords: FlagRecordDependencyMarket[]
  dependentUserRightRecords: FlagRecordDependencyMarket[]
}

export type FlagRecordEditableFields = FlagRecordFields & FlagRecordDependencyFields

export type FlagRecordMutationData = FlagRecordFields &
  Partial<Pick<FlagRecord, 'entryId' | 'created' | 'createdBy' | 'modified' | 'modifiedBy'>>

export type FlagRecordTypeOption = {
  label: string
  value: string
}

export const FLAG_RECORD_TYPE_OPTIONS: FlagRecordTypeOption[] = [
  {
    label: flagManagementTranslation.typeOptions.programs,
    value: 'Programs',
  },
  {
    label: flagManagementTranslation.typeOptions.accreditations,
    value: 'Accreditations',
  },
  {
    label: flagManagementTranslation.typeOptions.tools,
    value: 'Tools',
  },
  {
    label: flagManagementTranslation.typeOptions.userRights,
    value: 'UserRights',
  },
  {
    label: flagManagementTranslation.typeOptions.campaigns,
    value: 'Campaigns',
  },
]

const normalizeFlagRecordType = (value: string) =>
  value.trim().toLocaleLowerCase().replace(/\s+/g, '')

export const isToolFlagRecordType = (value: string) => normalizeFlagRecordType(value) === 'tools'

export const isUserRightsFlagRecordType = (value: string) =>
  normalizeFlagRecordType(value) === 'userrights'

export const isCampaignFlagRecordType = (value: string) => {
  const normalizedType = normalizeFlagRecordType(value)

  return normalizedType === 'campaign' || normalizedType === 'campaigns'
}

export const isEpiFlagRecordType = (value: string) => {
  const normalizedType = normalizeFlagRecordType(value)

  return (
    normalizedType === 'program' ||
    normalizedType === 'programs' ||
    normalizedType === 'accreditation' ||
    normalizedType === 'accreditations'
  )
}

export const canEditFlagRecordDependencies = (value: string) =>
  isToolFlagRecordType(value) || isUserRightsFlagRecordType(value) || isEpiFlagRecordType(value)

export const getReverseFlagRecordDependencyType = (
  value: string
): FlagRecordDependencyType | null => {
  if (isUserRightsFlagRecordType(value)) {
    return 'UserRights'
  }

  return isEpiFlagRecordType(value) ? 'EPI' : null
}

export const getFlagRecordAssignmentScope = (value: string): FlagRecordAssignmentScope | null => {
  if (isEpiFlagRecordType(value)) {
    return {
      source: 'WW_PartnerFlags',
      subjectLabel: 'Partners',
    }
  }

  if (isToolFlagRecordType(value)) {
    return {
      source: 'WW_User_Tools_Access',
      subjectLabel: 'Contacts',
    }
  }

  if (isUserRightsFlagRecordType(value)) {
    return {
      source: 'WW_UserRights',
      subjectLabel: 'Contacts',
    }
  }

  if (isCampaignFlagRecordType(value)) {
    return {
      source: 'WW_ContactCampaigns',
      subjectLabel: 'Contacts',
    }
  }

  return null
}
